import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  IconButton,
  Badge,
  Popover,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  Divider,
  Tooltip
} from '@mui/material';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { apiFetch } from '../../api/client';
import CalendarioDialog from '../calendario/CalendarioDialog';
import TarefasPanel from '../tarefas/TarefasPanel';
import useContratosAtividadesPendentes from '../../hooks/useContratosAtividadesPendentes';
import PendingActionsIcon from '@mui/icons-material/PendingActions';

export default function TopBarActions() {
  const navigate = useNavigate();
  const location = useLocation();
  const { pendentes: atividadesPendentes, total: totalAtividadesPendentes } = useContratosAtividadesPendentes();
  const [alertas, setAlertas] = useState([]);
  const [notifAnchor, setNotifAnchor] = useState(null);
  const [calOpen, setCalOpen] = useState(false);
  const [tarefasOpen, setTarefasOpen] = useState(false);
  const [gmailUnread, setGmailUnread] = useState(0);

  const carregarAlertas = useCallback(async () => {
    try {
      const res = await apiFetch('/api/alertas');
      if (res.ok) {
        const data = await res.json();
        setAlertas(Array.isArray(data) ? data : []);
      }
    } catch {
      setAlertas([]);
    }
  }, []);

  useEffect(() => {
    carregarAlertas();
    const interval = setInterval(carregarAlertas, 60000);
    const handleContratosRefresh = () => carregarAlertas();
    window.addEventListener('contratos:refresh', handleContratosRefresh);
    return () => {
      clearInterval(interval);
      window.removeEventListener('contratos:refresh', handleContratosRefresh);
    };
  }, [carregarAlertas]);

  useEffect(() => {
    let cancelled = false;
    const loadUnread = async () => {
      try {
        const res = await apiFetch('/api/gmail/unread-count');
        const data = await res.json().catch(() => ({}));
        if (!cancelled) setGmailUnread(data.connected ? Number(data.count || 0) : 0);
      } catch {
        if (!cancelled) setGmailUnread(0);
      }
    };
    loadUnread();
    const timer = setInterval(loadUnread, 120000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [location.pathname]);

  const naoLidos = alertas.filter((a) => !a.lido).length;
  const badgeTotal = naoLidos + totalAtividadesPendentes;

  const abrirAlerta = async (alerta) => {
    if (!alerta.lido) {
      await apiFetch(`/api/alertas/${alerta.id}/lido`, { method: 'PATCH' });
      carregarAlertas();
    }
    setNotifAnchor(null);
    if (alerta.link) navigate(alerta.link);
  };

  const marcarTodos = async () => {
    await apiFetch('/api/alertas/marcar-todos-lidos', { method: 'PATCH' });
    carregarAlertas();
  };

  const fetchEventos = async (params) => {
    const q = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v != null && v !== '') q.set(k, String(v));
    });
    const res = await apiFetch(`/api/calendario/eventos?${q.toString()}`);
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Erro ao carregar calendário');
    }
    return res.json();
  };

  const atualizarEvento = async (id, payload) => {
    const res = await apiFetch(`/api/calendario/eventos/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Erro ao atualizar evento');
    carregarAlertas();
  };

  const excluirEvento = async (id) => {
    const res = await apiFetch(`/api/calendario/eventos/${id}`, { method: 'DELETE' });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Erro ao excluir evento');
    carregarAlertas();
  };

  const fetchUsuarios = async () => {
    const res = await apiFetch('/api/calendario/usuarios-destino');
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Erro ao carregar usuários');
    return data;
  };

  const fetchPrestadores = async () => {
    const res = await apiFetch('/api/calendario/prestadores-destino');
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Erro ao carregar prestadores');
    return data;
  };

  const criarEvento = async (payload) => {
    const res = await apiFetch('/api/calendario/eventos', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Erro ao criar evento');
    carregarAlertas();
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
      <Tooltip title="Calendário / agenda">
        <IconButton onClick={() => setCalOpen(true)} sx={{ color: 'primary.main' }} aria-label="Calendário">
          <CalendarTodayIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="E-mail">
        <IconButton
          onClick={() => navigate('/email')}
          sx={{
            color: 'primary.main',
            bgcolor: location.pathname === '/email' ? 'rgba(25,118,210,0.12)' : 'transparent',
          }}
          aria-label="E-mail"
        >
          <Badge badgeContent={gmailUnread} color="error" max={99}>
            <MailOutlineIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Tooltip title="Tarefas">
        <IconButton
          onClick={() => setTarefasOpen(true)}
          sx={{ color: 'primary.main' }}
          aria-label="Tarefas"
        >
          <TaskAltIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="Alertas e notificações">
        <IconButton
          onClick={(e) => setNotifAnchor(e.currentTarget)}
          sx={{ color: 'primary.main' }}
          aria-label="Notificações"
        >
          <Badge badgeContent={badgeTotal} color="error" max={99}>
            <NotificationsNoneIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(notifAnchor)}
        anchorEl={notifAnchor}
        onClose={() => setNotifAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 1, minWidth: 320, maxWidth: 400 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1, py: 0.5 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              Notificações
            </Typography>
            {naoLidos > 0 && (
              <Button size="small" onClick={marcarTodos} sx={{ textTransform: 'none' }}>
                Marcar todas lidas
              </Button>
            )}
          </Box>
          <Divider />
          {totalAtividadesPendentes > 0 && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.75 }}>
                <PendingActionsIcon sx={{ color: '#ef6c00', fontSize: 18 }} />
                <Typography variant="subtitle2" fontWeight={700}>
                  Contratos — pendentes ({totalAtividadesPendentes})
                </Typography>
              </Box>
              <List dense sx={{ maxHeight: 200, overflow: 'auto', mb: 1 }}>
                {atividadesPendentes.slice(0, 8).map((a) => (
                  <ListItem
                    key={`atv-${a.id}`}
                    sx={{ bgcolor: 'rgba(239,108,0,0.06)', cursor: 'pointer', borderRadius: 1, mb: 0.5 }}
                    onClick={() => {
                      setNotifAnchor(null);
                      navigate('/contratos/atividades', {
                        state: { highlightAtividadeId: a.id, highlightProcessoId: a.processoId }
                      });
                    }}
                  >
                    <ListItemText
                      primary={a.titulo || a.solicitacaoTitulo || 'Atividade pendente'}
                      secondary={[a.processoCodigo, a.destinoLabel || a.destino].filter(Boolean).join(' · ')}
                      primaryTypographyProps={{ fontWeight: 700, fontSize: '0.85rem' }}
                    />
                  </ListItem>
                ))}
              </List>
              {location.pathname.startsWith('/contratos') && (
                <Button
                  size="small"
                  fullWidth
                  onClick={() => { setNotifAnchor(null); navigate('/contratos/analise-comparativa'); }}
                  sx={{ mb: 1, textTransform: 'none' }}
                >
                  Abrir Análise Comparativa
                </Button>
              )}
              <Divider />
            </>
          )}
          <List dense sx={{ maxHeight: 360, overflow: 'auto' }}>
            {alertas.length === 0 ? (
              <ListItem>
                <ListItemText primary="Nenhum alerta no momento." secondary="Você está em dia." />
              </ListItem>
            ) : (
              alertas.map((a) => (
                <ListItem
                  key={a.id}
                  sx={{ bgcolor: a.lido ? 'transparent' : 'action.hover', cursor: 'pointer' }}
                  onClick={() => abrirAlerta(a)}
                >
                  <ListItemText
                    primary={a.titulo}
                    secondary={
                      <>
                        {a.mensagem}
                        {a.createdAt && (
                          <Typography component="span" variant="caption" display="block" color="text.secondary">
                            {new Date(a.createdAt).toLocaleString('pt-BR')}
                          </Typography>
                        )}
                      </>
                    }
                    primaryTypographyProps={{ fontWeight: a.lido ? 400 : 700, fontSize: '0.9rem' }}
                  />
                </ListItem>
              ))
            )}
          </List>
          <Button size="small" fullWidth onClick={() => { setNotifAnchor(null); setCalOpen(true); }} sx={{ mt: 1 }}>
            Abrir calendário completo
          </Button>
        </Box>
      </Popover>

      <CalendarioDialog
        open={calOpen}
        onClose={() => setCalOpen(false)}
        fetchEventos={fetchEventos}
        fetchUsuarios={fetchUsuarios}
        fetchPrestadores={fetchPrestadores}
        criarEvento={criarEvento}
        atualizarEvento={atualizarEvento}
        excluirEvento={excluirEvento}
      />

      {tarefasOpen && (
        <TarefasPanel open={tarefasOpen} onClose={() => setTarefasOpen(false)} modo="isg" />
      )}
    </Box>
  );
}
